import React, { useContext, useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { NavigationContext } from "../../../context/navigation-context";
import useFetch from "../../../hooks/fetchMSSQL-hook";
import NoResults from "../../../components/NoResults/NoResults";
import SearchFilter from "../../../components/Filter/SearchFilter";
import moment from "moment";

const sourceStyle = {
  TRX: { label: "Transakcja", background: "#e7f5ec", color: "#198754" },
  PORTAL: { label: "Portal", background: "#e8f1ff", color: "#0d6efd" },
  AUTOMAT: { label: "Automat", background: "#fff3d6", color: "#9a6700" },
  VISIT: { label: "Wizyta", background: "#f0e8ff", color: "#7044ac" },
};

const stateTypes = ["E", "U", "P", "K", "R"];

const getStateDifference = (log) => {
  if (!stateTypes.includes(log?.Type)) return null;
  if (log?.BeforeValue == null || log?.AfterValue == null) return null;

  const before = Number(log.RawBeforeValue ?? log.BeforeValue);
  const after = Number(log.RawAfterValue ?? log.AfterValue);

  if (!Number.isFinite(before) || !Number.isFinite(after)) return null;

  const difference = Number((after - before).toFixed(4));
  return difference === 0 ? "0" : `${difference > 0 ? "+" : ""}${difference}`;
};

const MachineLogs = ({ machineData }) => {
  const { fetchMssqlApi } = useFetch();
  const { setHeaderData } = useContext(NavigationContext);
  const [machineLogs, setMachineLogs] = useState([]);

  const defaultFilter = useMemo(() => ({
    page: 1,
    rowsPerPage: 1000,
    dateTimeRangeFrom: moment().startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    dateTimeRangeTo: moment().endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    machineFeederNo: "",
    machineLogType: "",
    machineLogSource: "",
  }), []);

  const getMachineLogs = (f) => {
    fetchMssqlApi("machine-logs", {
      method: "POST",
      hideNotification: true,
      data: {
        MachineId: machineData?.MachineId,
        CreateDateTimeFrom: f?.dateTimeRangeFrom,
        CreateDateTimeTo: f?.dateTimeRangeTo,
        MachineFeederNo: f?.machineFeederNo || null,
        MachineLogType: f?.machineLogType || null,
        MachineLogSource: f?.machineLogSource || null,
      },
      showLoader: true,
    }, (response) => setMachineLogs(response));
  };

  useEffect(() => {
    setHeaderData({ text: "Historia zmian automatu" });
  }, []);

  return (
    <div className="container-fluid p-0 filter-icon-fix">
      <SearchFilter
        filterName="machineLogs"
        defaultFilter={defaultFilter}
        reportList={[{ reportId: 96, reportColor: "dark" }]}
        search={getMachineLogs}
        totalCount={machineLogs?.length}
        forNow
        machineIdsFromCheckbox={[machineData?.MachineId]}
      />

      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
          <div>
            <span className="fw-bold">{machineData?.MachineName}</span>
            <span className="text-muted ms-2">{machineData?.SerialNo}</span>
          </div>

          <span className="badge bg-light text-dark border px-3 py-2">
            {machineLogs?.length || 0} zmian
          </span>
        </div>

        <div className="card-body p-0 overflow-auto custom-scroll" style={{ maxHeight: "80vh" }}>
          {machineLogs?.length ? (
            <>
              <div className="d-flex align-items-center px-3 py-2 border-bottom bg-light small fw-semibold text-muted text-nowrap" style={{ minWidth: 1630 }}>
                <div style={{ width: 155, flexShrink: 0 }}>Data</div>
                <div style={{ width: 125, flexShrink: 0 }}>Źródło</div>
                <div style={{ width: 80, flexShrink: 0 }}>Wybór</div>
                <div style={{ width: 245, flexShrink: 0 }}>Produkt / receptura</div>
                <div style={{ width: 145, flexShrink: 0 }}>EAN</div>
                <div style={{ width: 205, flexShrink: 0 }}>Zmiana</div>
                <div style={{ width: 220, flexShrink: 0 }}>Dokument</div>
                <div className="ms-auto" style={{ width: 360, flexShrink: 0 }}>Przed → po</div>
              </div>

              {machineLogs.map((log, idx) => {
                const source = sourceStyle[log?.Source] || {
                  label: log?.Source || "-",
                  background: "#f1f3f5",
                  color: "#6c757d",
                };

                const isStateProblem = log?.Change === "Problem z stanami";
                const hasValues = log?.BeforeValue != null || log?.AfterValue != null;
                const documentPath = log?.DocumentPath || null;
                const documentName = log?.MachineInventoryFullName || (log?.MachineInventoryId != null ? `#${log.MachineInventoryId}` : null);
                const difference = getStateDifference(log);
                const differenceColor = difference === "0" ? "text-muted" : difference?.startsWith("-") ? "text-danger" : "text-success";

                return (
                  <div
                    key={log?.MachineLogId ?? idx}
                    className="d-flex align-items-center px-3 py-2 border-bottom small text-nowrap"
                    style={{ minWidth: 1630, background: isStateProblem ? "#fff8f8" : undefined }}
                  >
                    <div className="text-muted" style={{ width: 155, flexShrink: 0 }}>
                      {log?.ChangeDateTime ? moment(log.ChangeDateTime).format("DD.MM.YYYY HH:mm:ss") : "-"}
                    </div>

                    <div style={{ width: 125, flexShrink: 0 }}>
                      <span className="px-2 py-1 rounded fw-semibold" style={{ background: source.background, color: source.color }}>
                        {source.label}
                      </span>
                    </div>

                    <div className="fw-semibold" style={{ width: 80, flexShrink: 0 }}>
                      {log?.MachineFeederNo || "-"}
                    </div>

                    <div className="text-truncate pe-3 fw-semibold" style={{ width: 245, flexShrink: 0 }} title={log?.ProductName || ""}>
                      {log?.ProductName || "-"}
                    </div>

                    <div className="text-muted text-truncate pe-3" style={{ width: 145, flexShrink: 0 }} title={log?.ProductEAN || ""}>
                      {log?.ProductEAN || "-"}
                    </div>

                    <div className={isStateProblem ? "fw-bold text-danger" : "fw-semibold"} style={{ width: 205, flexShrink: 0 }}>
                      {log?.Change || "-"}
                    </div>

                    <div style={{ width: 220, flexShrink: 0 }}>
                      {documentPath ? (
                        <Link
                          to={documentPath}
                          className={`btn text-nowrap btn-sm btn-outline-${["danger", "info", "secondary"].includes(log?.DocumentColor) ? log.DocumentColor : "primary"}`}
                          title={`Otwórz dokument ${documentName}`}
                        >
                          {documentName}
                        </Link>
                      ) : (
                        <span className="text-muted">{documentName || "-"}</span>
                      )}
                    </div>

                    <div className="d-flex align-items-center ms-auto" style={{ width: 360, flexShrink: 0 }}>
                      {hasValues ? (
                        <>
                          <span
                            className={`text-truncate ${isStateProblem ? "fw-bold text-danger" : ""}`}
                            style={{ maxWidth: 125 }}
                            title={log?.BeforeValue ?? ""}
                          >
                            {log?.BeforeValue ?? "-"}
                          </span>

                          <span className="mx-2 text-muted">→</span>

                          <span
                            className={`text-truncate ${isStateProblem ? "fw-bold text-danger" : "fw-semibold"}`}
                            style={{ maxWidth: 125 }}
                            title={log?.AfterValue ?? ""}
                          >
                            {log?.AfterValue ?? "-"}
                          </span>

                          {difference != null && (
                            <span className={`ms-2 small fw-semibold ${differenceColor}`}>
                              ({difference})
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <div className="p-3">
              <NoResults />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MachineLogs;
